import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as loginApi, logout as logoutApi, type LoginRequest } from '@/api/auth'
import { clubApi } from '@/api/club'
import { memberApi } from '@/api/member'

/** JWT payload에서 userSeq 추출 (SNS 등 userSeq 미저장 세션 호환) */
function parseUserSeqFromJwt(token: string | null): number | null {
  if (!token) return null
  const parts = token.split('.')
  const payloadPart = parts[1]
  if (payloadPart == null) return null
  try {
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '==='.slice((base64.length + 3) % 4)
    const json = JSON.parse(atob(padded)) as { userSeq?: string | number }
    const raw = json.userSeq
    if (raw == null || raw === '') return null
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10)
    return Number.isFinite(n) ? n : null
  } catch {
    // v12-ok: JWT payload 파싱 폴백 — userSeq 없음으로 처리
    return null
  }
}

function readInitialUserSeq(): number | null {
  const stored = localStorage.getItem('userSeq')
  if (stored) {
    const n = parseInt(stored, 10)
    if (Number.isFinite(n)) return n
  }
  return parseUserSeqFromJwt(localStorage.getItem('accessToken'))
}

export interface UserClub {
  clubSeq: number
  memberRole: string
  [key: string]: any
}

export interface User {
  userId: string
  userName: string
  combinedNickName?: string
  userType: string
  clubSeq?: number
  clubs?: UserClub[]
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const accessToken = ref<string | null>(localStorage.getItem('accessToken'))
  const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'))
  const userId = ref<string | null>(localStorage.getItem('userId'))
  const userName = ref<string | null>(localStorage.getItem('userName'))
  const combinedNickName = ref<string | null>(localStorage.getItem('combinedNickName'))
  const userType = ref<string | null>(localStorage.getItem('userType'))
  const clubManagerYn = ref<string | null>(localStorage.getItem('clubManagerYn'))
  const userImg = ref<string | null>(localStorage.getItem('userImg'))
  const userSeq = ref<number | null>(readInitialUserSeq())
  const clubMemberships = ref<number[]>([])
  const clubRoles = ref<Record<number, string>>({})
  // 멤버십을 서버에서 1회 이상 조회했는지 — 새로고침 직후 빈 배열을 '미가입'으로 오판하지 않기 위함
  const membershipsLoaded = ref(false)

  // User object
  const user = computed<User | null>(() => {
    if (!userId.value) return null
    return {
      userId: userId.value,
      userName: userName.value || '',
      combinedNickName: combinedNickName.value || undefined,
      userType: userType.value || '',
      clubSeq: clubMemberships.value[0], // 첫 번째 동호회를 기본값으로
      clubs: clubMemberships.value.map(seq => ({
        clubSeq: seq,
        memberRole: clubRoles.value[seq] || 'MEMBER',
      })),
    }
  })

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value)

  // Actions
  const login = async (credentials: LoginRequest) => {
    try {
      const response = await loginApi(credentials)

      if ((response.success || response.code === 200) && response.data) {
        // 토큰 저장
        accessToken.value = response.data.accessToken
        refreshToken.value = response.data.refreshToken
        userId.value = response.data.userId
        userName.value = response.data.userName
        userType.value = response.data.userType
        const rawCn = (response.data as any).combinedNickName || null
        // 잘못된 형태(앞에 ( 또는 뒤에 () 붙음)인 경우 사용하지 않음
        combinedNickName.value = (rawCn && !rawCn.startsWith('(') && !rawCn.endsWith('()')) ? rawCn : null

        // localStorage에 저장
        localStorage.setItem('accessToken', response.data.accessToken)
        localStorage.setItem('refreshToken', response.data.refreshToken)
        localStorage.setItem('userId', response.data.userId)
        localStorage.setItem('userName', response.data.userName)
        localStorage.setItem('userType', response.data.userType)
        const imgUrl = (response.data as any).userImg || (response.data as any).profileImageUrl || null
        userImg.value = imgUrl
        if (imgUrl) localStorage.setItem('userImg', imgUrl)
        else localStorage.removeItem('userImg')
        if (combinedNickName.value) {
          localStorage.setItem('combinedNickName', combinedNickName.value)
        } else {
          localStorage.removeItem('combinedNickName')
        }

        const seq = response.data.userSeq
        if (seq != null && Number.isFinite(Number(seq))) {
          userSeq.value = Number(seq)
          localStorage.setItem('userSeq', String(userSeq.value))
        } else {
          userSeq.value = parseUserSeqFromJwt(response.data.accessToken)
          if (userSeq.value != null) {
            localStorage.setItem('userSeq', String(userSeq.value))
          }
        }

        // 총무 의향 여부 저장
        clubManagerYn.value = response.data.clubManagerYn || 'N'
        localStorage.setItem('clubManagerYn', clubManagerYn.value)

        // 동호회 목록 로드 (비동기 - 로그인 완료를 블로킹하지 않음)
        loadClubMemberships()

        return { success: true, message: response.message }
      } else {
        return { success: false, message: response.message || '로그인에 실패했습니다.' }
      }
    } catch (error: any) {
      // v12-ok: 실패를 { success:false, message } 로 돌려주면 로그인 화면이 그 문구를 띄운다
      const message = error.response?.data?.message || '로그인 중 오류가 발생했습니다.'
      return { success: false, message }
    }
  }

  const clearSession = () => {
    accessToken.value = null
    refreshToken.value = null
    userId.value = null
    userName.value = null
    combinedNickName.value = null
    userType.value = null
    clubManagerYn.value = null
    userImg.value = null
    userSeq.value = null
    clubMemberships.value = []
    clubRoles.value = {}
    membershipsLoaded.value = false
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userId')
    localStorage.removeItem('userName')
    localStorage.removeItem('combinedNickName')
    localStorage.removeItem('userType')
    localStorage.removeItem('userSeq')
    localStorage.removeItem('clubManagerYn')
    localStorage.removeItem('userImg')
    localStorage.removeItem('clubMemberships')
    localStorage.removeItem('clubRoles')
  }

  const logout = async () => {
    try {
      await logoutApi()
    } catch (error) {
      // v12-ok: 로그아웃 API 실패 — 로컬 토큰·상태는 finally 에서 지우므로 사용자에겐 로그아웃이 완료된다
      console.error('Logout API error:', error)
    } finally {
      // 상태 초기화
      accessToken.value = null
      refreshToken.value = null
      userId.value = null
      userName.value = null
      userType.value = null
      clubManagerYn.value = null
      userImg.value = null
      userSeq.value = null
      clubMemberships.value = []
      clubRoles.value = {}
      membershipsLoaded.value = false

      // localStorage 클리어
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('userId')
      localStorage.removeItem('userName')
      localStorage.removeItem('userType')
      localStorage.removeItem('userSeq')
      localStorage.removeItem('clubManagerYn')
      localStorage.removeItem('userImg')
      localStorage.removeItem('clubMemberships')
      localStorage.removeItem('clubRoles')
    }
  }

  const checkAuth = () => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      logout()
      return false
    }
    return true
  }

  /**
   * 특정 동호회의 회원인지 확인
   * @param clubId 동호회 ID
   * @returns 회원 여부
   */
  const isClubMember = (clubId: number): boolean => {
    return clubMemberships.value.includes(clubId)
  }

  /**
   * 로그인 사용자의 동호회 목록 로드
   */
  const loadClubMemberships = async () => {
    try {
      if (!isAuthenticated.value) {
        clubMemberships.value = []
        return
      }

      const response = await clubApi.getMyClubs()
      if (response.code === 200 && response.data) {
        clubMemberships.value = response.data.map((club: any) => club.clubSeq)
        const roles: Record<number, string> = {}
        response.data.forEach((club: any) => {
          if (club.clubSeq && club.memberRole) {
            roles[club.clubSeq] = club.memberRole
          }
        })
        clubRoles.value = roles
        // localStorage에 저장
        localStorage.setItem('clubMemberships', JSON.stringify(clubMemberships.value))
        localStorage.setItem('clubRoles', JSON.stringify(clubRoles.value))
        // 성공했을 때만 «불러왔다» 로 못박는다 — 실패를 성공으로 세면 다시 시도하지 않는다
        membershipsLoaded.value = true
      }
    } catch (error) {
      // v12-ok: 배경 조회(소속 동호회) — 기존 값을 유지하고 membershipsLoaded 를 세우지 않아 다음에 재시도
      console.error('Failed to load club memberships:', error)
      /*
       * ⛔ 2026-09-08 — 여기서 `clubMemberships = []` 로 지우고 `membershipsLoaded = true` 로
       *    못박고 있었다. 그러면 **일시적 실패 한 번에 세션 내내 «비회원»** 이 된다.
       *    라우터 가드(`needsClubContext` → `ensureClubMemberships`)가 이 값을 보고 판정하므로
       *    총무가 동호회 화면에 못 들어가고 전부 소개 페이지로 튕긴다.
       *    실측(2026-09-08): `/api/club/my` 를 500 으로 막으니 라운딩 상세가
       *    `/mobile/club/intro` 로 튕겼다.
       *
       *    실패는 «소속 없음» 이 아니다. 직전 값(restoreClubMemberships 가 복원한 localStorage 분)을
       *    그대로 두고, 다음 진입에서 다시 시도하게 둔다.
       */
      membershipsLoaded.value = false
    }
  }

  // 가드 등에서 멤버십 로드를 보장 (세션당 1회). 새로고침 직후 clubMemberships가 비어
  // 동호회 보유 사용자가 GuestHome으로 잘못 분기되는 것을 방지한다.
  /** 실패가 이어질 때 매 네비게이션마다 두드리지 않도록 잠깐 쉰다 */
  const membershipRetryAt = ref(0)

  const ensureClubMemberships = async () => {
    if (!isAuthenticated.value) return
    if (membershipsLoaded.value) return
    if (Date.now() < membershipRetryAt.value) return
    membershipRetryAt.value = Date.now() + 5000
    await loadClubMemberships()
  }

  /**
   * localStorage에서 동호회 목록 복원
   */
  /**
   * 특정 동호회에서의 역할 조회
   */
  const getClubRole = (clubId: number): string | null => {
    return clubRoles.value[clubId] || null
  }

  /**
   * 조회·관리뷰·승인 권한 (C-21): 회장 + 총무·부총무.
   * ⚠️ 2026-07-21 통화: 부회장 = 일반회원 권한(승인·관리뷰 제외). 회장만 승인.
   * ⚠️ 쓰기(생성·수정·삭제·정산·결제)에는 쓰지 말 것 — 회장은 조회·승인만. 쓰기는 isClubOperator.
   */
  const isClubAdmin = (clubId: number): boolean => {
    const role = clubRoles.value[clubId]
    return ['PRESIDENT', 'MANAGER', 'VICE_MANAGER'].includes(role || '')
  }

  /**
   * 쓰기(운영) 권한 (C-21): 총무·부총무만 (회장·부회장 제외).
   * 생성·수정·삭제·정산·결제·환불 등 모든 쓰기 작업 게이트.
   */
  const isClubOperator = (clubId: number): boolean => {
    const role = clubRoles.value[clubId]
    return ['MANAGER', 'VICE_MANAGER'].includes(role || '')
  }
  /** @deprecated isClubOperator 사용 — 하위호환 별칭 */
  const isClubManager = isClubOperator

  const restoreClubMemberships = () => {
    const stored = localStorage.getItem('clubMemberships')
    if (stored) {
      try {
        clubMemberships.value = JSON.parse(stored)
      } catch {
        // v12-ok: localStorage JSON.parse 폴백 — 빈 목록으로 두고 서버에서 다시 불러온다
        clubMemberships.value = []
      }
    }
    const storedRoles = localStorage.getItem('clubRoles')
    if (storedRoles) {
      try {
        clubRoles.value = JSON.parse(storedRoles)
      } catch {
        // v12-ok: localStorage JSON.parse 폴백 — 빈 역할로 두고 서버에서 다시 불러온다
        clubRoles.value = {}
      }
    }
  }

  /**
   * JWT 토큰 직접 세팅 (SNS 로그인 등 외부에서 토큰을 받는 경우)
   */
  const setTokens = (newAccessToken: string, newRefreshToken: string) => {
    accessToken.value = newAccessToken
    refreshToken.value = newRefreshToken
    localStorage.setItem('accessToken', newAccessToken)
    localStorage.setItem('refreshToken', newRefreshToken)
  }

  /**
   * 사용자 정보 직접 세팅 (SNS 로그인 등)
   */
  const setUser = (info: {
    userId: string
    userName: string
    userType?: string
    clubManagerYn?: string
    userSeq?: number | string
  }) => {
    userId.value = info.userId
    userName.value = info.userName
    userType.value = info.userType || ''
    clubManagerYn.value = info.clubManagerYn || 'N'
    localStorage.setItem('userId', info.userId)
    localStorage.setItem('userName', info.userName)
    localStorage.setItem('userType', info.userType || '')
    localStorage.setItem('clubManagerYn', info.clubManagerYn || 'N')

    if (info.userSeq != null && info.userSeq !== '') {
      const n = typeof info.userSeq === 'number' ? info.userSeq : parseInt(String(info.userSeq), 10)
      if (Number.isFinite(n)) {
        userSeq.value = n
        localStorage.setItem('userSeq', String(n))
        return
      }
    }
    userSeq.value = parseUserSeqFromJwt(accessToken.value)
    if (userSeq.value != null) {
      localStorage.setItem('userSeq', String(userSeq.value))
    } else {
      localStorage.removeItem('userSeq')
    }
  }

  // 초기화 시 동호회 목록 복원
  restoreClubMemberships()

  /**
   * 앱 진입 시 사용자 정보 갱신 (clubManagerYn 등 최신 상태 반영)
   */
  const refreshUserInfo = async () => {
    if (!isAuthenticated.value) return
    try {
      const { data: res } = await memberApi.getMyInfo()
      const payload = res as { code?: number; data?: Record<string, unknown> }
      if (payload.code === 200 && payload.data) {
        const data = payload.data
        const cm = data.clubManagerYn
        if (typeof cm === 'string' && cm) {
          clubManagerYn.value = cm
          localStorage.setItem('clubManagerYn', cm)
        }
        const un = data.userName
        if (typeof un === 'string' && un) {
          userName.value = un
          localStorage.setItem('userName', un)
        }
        const cnn = data.combinedNickName
        if (typeof cnn === 'string' && cnn) {
          combinedNickName.value = cnn
          localStorage.setItem('combinedNickName', cnn)
        }
        const img = data.profileImageUrl
        if (typeof img === 'string') {
          userImg.value = img || null
          if (img) localStorage.setItem('userImg', img)
          else localStorage.removeItem('userImg')
        }
      }
    } catch (error) {
      // v12-ok: 배경 재조회(사용자 정보 갱신) — 기존 값 유지
      console.error('Failed to refresh user info:', error)
    }
  }

  return {
    // State
    accessToken,
    refreshToken,
    userId,
    userName,
    combinedNickName,
    userType,
    userImg,
    userSeq,
    clubManagerYn,
    clubMemberships,
    clubRoles,
    membershipsLoaded,
    user,
    // Getters
    isAuthenticated,
    // Actions
    login,
    logout,
    clearSession,
    checkAuth,
    setTokens,
    setUser,
    isClubMember,
    isClubAdmin,
    isClubOperator,
    isClubManager,
    getClubRole,
    loadClubMemberships,
    ensureClubMemberships,
    refreshUserInfo,
  }
})
